import { getIn } from "final-form";
import orderBy from "lodash/orderBy";
import React from "react";
import { useField, useForm } from "react-final-form";

import { Autocomplete, AutocompleteProps, CircularProgress, styled, Typography } from "@mui/material";

import { Tooltip } from "../../Tooltip";
import { Field as TextField } from "../TextField";

import type {
    BaseSelectFieldProps,
    ControlledMultipleSelectFieldProps,
    ControlledSelectFieldProps,
    ControlledSelectValue,
    ControlledSingleSelectFieldProps,
    PrimitiveOptionValue,
    SelectFieldProps,
    SelectOption,
} from "./SelectField.types";

const StyledTextField = styled(TextField, {
    shouldForwardProp: (prop) => prop !== "searchable",
})<{ searchable?: boolean }>(({ searchable }) => ({
    "& .MuiInputBase-input": searchable
        ? {}
        : {
              caretColor: "transparent",
              cursor: "pointer",
          },
}));

function BaseSelectField<IsMultiple extends boolean>(props: BaseSelectFieldProps<IsMultiple>): React.JSX.Element {
    const {
        disableSorting: _ignoredDisableSorting,
        disabledTooltip,
        errorMessage,
        sortedOptions,
        disableClearable,
        normalizedValue,
        placeholder,
        searchable,
        ...rest
    } = props;

    const selectField = (
        <Autocomplete<SelectOption, IsMultiple, boolean, false>
            {...rest}
            value={normalizedValue}
            disableClearable={disableClearable}
            options={sortedOptions}
            groupBy={(option) => option.category || ""}
            getOptionLabel={(option) => option.label}
            slotProps={{
                ...rest.slotProps,
                chip: {
                    size: "small",
                    sx: { height: 22, fontSize: 12 },
                },
            }}
            renderInput={(params) => (
                <StyledTextField
                    {...params}
                    searchable={searchable}
                    size="small"
                    error={Boolean(errorMessage)}
                    helperText={errorMessage}
                    label={props.label}
                    placeholder={placeholder}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {rest.loading ? <CircularProgress size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        },
                        inputLabel: { ...params.InputLabelProps },
                        htmlInput: { ...params.inputProps, readOnly: !searchable },
                    }}
                />
            )}
            renderOption={(optionProps, option) => {
                const { key: _key, ...restOptionProps } = optionProps;
                const depth = option.depth ?? 0;

                return (
                    <li key={option.value} {...restOptionProps}>
                        <Typography sx={{ pl: depth * 2, width: "100%" }}>{option.label}</Typography>
                    </li>
                );
            }}
        />
    );

    return (
        <Tooltip
            hide={!(rest.disabled && disabledTooltip)}
            title={disabledTooltip}
            childrenBoxStyles={rest.fullWidth ? { display: "block", width: "100%" } : undefined}
        >
            {selectField}
        </Tooltip>
    );
}

function SelectField(props: SelectFieldProps): React.JSX.Element {
    const { disableClearable = true, errorMessage, disableSorting } = props;

    const sortedOptions: SelectOption[] = React.useMemo(() => {
        return disableSorting ? [...props.options] : orderBy(props.options, [(option) => option.label.toLowerCase()], ["asc"]);
    }, [props.options, disableSorting]);

    if (props.multiple) {
        return (
            <BaseSelectField<true>
                {...props}
                normalizedValue={props.value ?? []}
                disableClearable={disableClearable}
                sortedOptions={sortedOptions}
                errorMessage={errorMessage}
            />
        );
    }

    return (
        <BaseSelectField<false>
            {...props}
            normalizedValue={props.value ?? null}
            disableClearable={disableClearable}
            sortedOptions={sortedOptions}
            errorMessage={errorMessage}
        />
    );
}

function getFieldErrorMessage(
    errors: Record<string, unknown> | undefined,
    fieldName: string,
    touched: boolean | undefined,
): string | undefined {
    if (!touched || !errors) {
        return undefined;
    }

    const nestedError = getIn(errors, fieldName);
    const flatError = errors[fieldName];
    const fieldError = nestedError ?? flatError;
    return typeof fieldError === "string" ? fieldError : undefined;
}

function ControlledMultipleSelectField(props: ControlledMultipleSelectFieldProps): React.JSX.Element {
    const form = useForm();
    const errors = form.getState().errors;
    const { input, meta } = useField<ControlledSelectValue<true>>(props.name, props.config);

    const getValue = (): SelectOption[] => {
        const selectedValues: PrimitiveOptionValue[] = Array.isArray(input.value) ? input.value : [];
        return props.options.filter((option) => selectedValues.includes(option.value));
    };

    const onChange: AutocompleteProps<SelectOption, true, boolean, false>["onChange"] = (_event, options) => {
        const selectedValues = options.map((item) => item.value);
        input.onChange(selectedValues);
        props.onChange?.(selectedValues);
    };

    return (
        <SelectField
            {...props}
            value={getValue()}
            onChange={onChange}
            onBlur={input.onBlur}
            onFocus={input.onFocus}
            errorMessage={getFieldErrorMessage(errors, props.name, meta.touched)}
        />
    );
}

function ControlledSingleSelectField(props: ControlledSingleSelectFieldProps): React.JSX.Element {
    const form = useForm();
    const errors = form.getState().errors;
    // cannot use meta.error from useField due to the limitations of react-final-form,
    // which does not recognize errors for fields with dot notation in meta.error
    const { input, meta } = useField<ControlledSelectValue<false>>(props.name, props.config);

    const getValue = (): SelectOption | null => {
        return props.options.find((option) => option.value === input.value) ?? null;
    };

    const onChange: AutocompleteProps<SelectOption, false, boolean, false>["onChange"] = (_event, option) => {
        const nextValue = option?.value;
        input.onChange(nextValue);
        props.onChange?.(nextValue);
    };

    return (
        <SelectField
            {...props}
            value={getValue()}
            onChange={onChange}
            onBlur={input.onBlur}
            onFocus={input.onFocus}
            errorMessage={getFieldErrorMessage(errors, props.name, meta.touched)}
        />
    );
}

function ControlledSelectField(props: ControlledSelectFieldProps): React.JSX.Element {
    if (props.multiple) {
        return <ControlledMultipleSelectField {...props} />;
    }

    return <ControlledSingleSelectField {...props} />;
}

export { ControlledSelectField, SelectField };
